import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext.tsx';
import { RealtimeEvent } from '../../shared/types.ts';

type EventHandler = (event: RealtimeEvent) => void;

interface RealtimeContextType {
  isConnected: boolean;
  onlineUserIds: Set<string>;
  joinChannel: (channelId: string) => void;
  leaveChannel: (channelId: string) => void;
  joinProject: (projectId: string) => void;
  leaveProject: (projectId: string) => void;
  sendTyping: (channelId: string, isTyping: boolean) => void;
  sendCallSignal: (payload: { type: string; channelId?: string; targetUserId?: string; [key: string]: any }) => void;
  subscribe: (handler: EventHandler) => () => void;
}

const RealtimeContext = createContext<RealtimeContextType>({
  isConnected: false,
  onlineUserIds: new Set(),
  joinChannel: () => {},
  leaveChannel: () => {},
  joinProject: () => {},
  leaveProject: () => {},
  sendTyping: () => {},
  sendCallSignal: () => {},
  subscribe: () => () => {},
});

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Set<EventHandler>>(new Set());
  const reconnectTimeoutRef = useRef<any>(null);

  const joinedChannelsRef = useRef<Set<string>>(new Set());
  const joinedProjectsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.port === '5173' ? 'localhost:3001' : window.location.host;
      const wsUrl = `${protocol}//${host}/ws?token=${encodeURIComponent(token || '')}&userId=${encodeURIComponent(user.id)}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        // Automatically re-join active channels and projects
        for (const chId of joinedChannelsRef.current) {
          try {
            ws.send(JSON.stringify({ type: 'join_channel', channelId: chId }));
          } catch (e) {}
        }
        for (const pId of joinedProjectsRef.current) {
          try {
            ws.send(JSON.stringify({ type: 'join_project', projectId: pId }));
          } catch (e) {}
        }
      };

      ws.onmessage = (event) => {
        try {
          const payload: RealtimeEvent = JSON.parse(event.data);

          if ((payload as any).type === 'initial_presence') {
            const initialIds = (payload as any).data?.onlineUserIds;
            if (Array.isArray(initialIds)) {
              setOnlineUserIds(new Set(initialIds));
            }
          }

          if (payload.type === 'presence_update') {
            setOnlineUserIds((prev) => {
              const updated = new Set(prev);
              if (payload.data.status === 'online') {
                updated.add(payload.data.userId);
              } else {
                updated.delete(payload.data.userId);
              }
              return updated;
            });
          }

          // Distribute event to all active subscribers
          for (const handler of handlersRef.current) {
            try {
              handler(payload);
            } catch (e) {
              console.error('Realtime handler error:', e);
            }
          }
        } catch (err) {
          // Non-json or ping/pong frame
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        // Auto-reconnect after 3s
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [token, user?.id]);

  const joinChannel = (channelId: string) => {
    joinedChannelsRef.current.add(channelId);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'join_channel', channelId }));
    }
  };

  const leaveChannel = (channelId: string) => {
    joinedChannelsRef.current.delete(channelId);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'leave_channel', channelId }));
    }
  };

  const joinProject = (projectId: string) => {
    joinedProjectsRef.current.add(projectId);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'join_project', projectId }));
    }
  };

  const leaveProject = (projectId: string) => {
    joinedProjectsRef.current.delete(projectId);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'leave_project', projectId }));
    }
  };

  const sendTyping = (channelId: string, isTyping: boolean) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && user) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        channelId,
        userName: user.fullName,
        isTyping,
      }));
    }
  };

  const sendCallSignal = (payload: { type: string; channelId?: string; targetUserId?: string; [key: string]: any }) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const { type: signalType, ...rest } = payload;
      wsRef.current.send(JSON.stringify({
        type: 'call_signal',
        signalType,
        ...rest,
      }));
    }
  };

  const subscribe = (handler: EventHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  };

  return (
    <RealtimeContext.Provider
      value={{
        isConnected,
        onlineUserIds,
        joinChannel,
        leaveChannel,
        joinProject,
        leaveProject,
        sendTyping,
        sendCallSignal,
        subscribe,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);
